import { XMarkIcon } from '@heroicons/react/20/solid'
import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'
import { useDealFilters } from '../../deals/DealFiltersProvider'
import ProductDetails from '../../shared/product/ProductDetails'
import ProductRow from '../../shared/product/ProductRow'
import useCartProducts from '../hooks/useCartProducts'
import {
  useAddToCartConfirmation,
  useAddToCartConfirmationOpsDispatch,
} from './AddToCartConfirmationProvider'
import AddToCartConfirmationTopRow from './AddToCartConfirmationTopRow'
import AddToCartConfirmationButtons from './AddToCartConfirmationButtons'
import SmBreakpoint from '@g4g/ui/src/utility/SmBreakpoint'
import { fillProductPrice, toSingleCartProduct } from '../../../utils/shopItemUtils'
import { Transition } from '@headlessui/react'
import { Price } from '../hooks/useCalculatedPrices'

type LocalConfirm = { shopItemId: string; wasUpdate?: boolean; prices?: Price } | undefined

/**
 * This needs to be used in conjuction with the
 * useAddToCartConfirmationTrigger hook. The added
 * shopItemId is passed to the AddToCartConfirmation state.
 *
 */
const AddToCartConfirmation: FC = () => {
  const { isReady, query } = useRouter()
  const { addedShopItemId, wasUpdate, addedShopItemPrices } = useAddToCartConfirmation()
  const [localConfirm, setLocalConfirm] = useState<LocalConfirm>()
  const dispatch = useAddToCartConfirmationOpsDispatch()
  const { products } = useCartProducts()
  const product = toSingleCartProduct(
    products.filter((p) => p.product.shopItemId === localConfirm?.shopItemId)
  )
  const [isVisible, setIsVisible] = useState(true)
  const filters = useDealFilters()

  // Causes a Warning: Can't perform a React state update on an unmounted component.
  // should be DEV only, because of the usage of next router for filters on dev
  useEffect(() => {
    // Persist the query every time a filter is changed
    // here so we can return back to it when the confirmation is triggered,
    // have to use location.search since we're not updating next Router.query in prod
    if (isReady) {
      dispatch({
        type: 'persist-query',
        query: process.env.NODE_ENV === 'development' ? query : window.location.search.slice(1),
      })
    }
  }, [filters, isReady, dispatch, query])

  useEffect(() => {
    if (addedShopItemId) {
      // Set the confirmation shopItemId locally to this
      // component so it disappears when we unmount
      setLocalConfirm({
        shopItemId: addedShopItemId,
        wasUpdate,
        prices: addedShopItemPrices,
      })
      // Reset the AddToCart confirmation message so the
      // message doesn't reappear when we go back in history, etc.
      dispatch({ type: 'reset' })
    }
  }, [addedShopItemId, wasUpdate, dispatch, addedShopItemPrices])

  return (
    <Transition appear={true} show={!!(product && isVisible)}>
      {/* Mobile Background overlay */}
      <Transition.Child
        className="fixed inset-0 z-50 backdrop-blur-sm bg-black/50 sm:hidden"
        enter="transition-opacity ease-linear duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="w-full h-full" onClick={() => setIsVisible(false)}></div>
      </Transition.Child>
      {/* Confirmation Message */}
      <Transition.Child
        className="fixed bottom-0 left-0 right-0 z-100 sm:mb-12 sm:ml-6 sm:relative sm:z-1 will-change-transform"
        enter="transition ease-in-out duration-300 transform"
        enterFrom="translate-y-full sm:-translate-y-6 sm:opacity-0"
        enterTo="translate-y-0 sm:translate-y-0 sm:opacity-100"
        leave="transition ease-in-out duration-300 transform"
        leaveFrom="translate-y-0 sm:translate-y-0 sm:opacity-100"
        leaveTo="translate-y-full sm:-translate-y-6 sm:opacity-0"
      >
        <div data-cy="shop-add-to-cart-confirmation">
          <div className="relative flex w-full p-4 pb-10 rounded-t-lg sm:rounded-lg sm:pt-0 sm:pb-4 sm:pr-0 bg-gray-1000 sm:bg-gray-300/10">
            <div className="flex flex-col flex-grow space-y-2">
              <AddToCartConfirmationTopRow wasUpdate={localConfirm?.wasUpdate} />
              {product && (
                <SmBreakpoint
                  max={
                    <>
                      <ProductDetails product={fillProductPrice(product, localConfirm?.prices)} />
                      <div className="flex sm:justify-end">
                        <AddToCartConfirmationButtons />
                      </div>
                    </>
                  }
                  min={
                    <ProductRow shine product={fillProductPrice(product, localConfirm?.prices)} />
                  }
                />
              )}
            </div>
            <div className="absolute top-0 right-0 sm:relative">
              <button onClick={() => setIsVisible(false)} className="p-2">
                <XMarkIcon className="w-6 h-6 text-gray-500 hover:text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      </Transition.Child>
    </Transition>
  )
}

export default AddToCartConfirmation
